import { CURRENCY_CODE } from '@/constants/common';

export type CurrencyCode = keyof typeof CURRENCY_CODE;

/**
 * Currency symbols accepted in imported CSV budget values.
 *
 * Covers every symbol the backend exporter (RoadmapCsvExport::getCurrencySymbol) writes for the
 * currencies the backend accepts, plus the frontend's own symbols for SAR, QAR and PKR. The
 * exporter writes QAR and PKR as their codes, which are accepted like any supported code.
 * Where one symbol is shared, the first currency is the default.
 */
export const CSV_CURRENCY_SYMBOLS: { currencies: CurrencyCode[]; symbol: string }[] = [
  { symbol: '£', currencies: ['GBP'] },
  { symbol: '$', currencies: ['USD'] },
  { symbol: '€', currencies: ['EUR'] },
  { symbol: 'A$', currencies: ['AUD'] },
  { symbol: 'C$', currencies: ['CAD'] },
  { symbol: 'HK$', currencies: ['HKD'] },
  { symbol: 'NZ$', currencies: ['NZD'] },
  { symbol: 'S$', currencies: ['SGD'] },
  { symbol: 'د.إ', currencies: ['AED'] },
  { symbol: '¥', currencies: ['JPY', 'CNY'] },
  { symbol: 'kr', currencies: ['DKK', 'NOK', 'SEK'] },
  { symbol: '₹', currencies: ['INR'] },
  { symbol: 'R', currencies: ['ZAR'] },
  { symbol: '﷼', currencies: ['SAR'] },
  { symbol: 'ر.س', currencies: ['SAR'] },
  { symbol: 'ر.ق', currencies: ['QAR'] },
  { symbol: '₨', currencies: ['PKR'] },
];

/**
 * Symbols the exporter can write for currencies the backend does not accept. Recognised only so
 * the user is told the currency is unsupported rather than that the value is malformed.
 */
const UNSUPPORTED_EXPORT_SYMBOLS = ['₩', '₺', '₽', 'R$', 'zł', '฿', 'RM', '₱', 'Kč', '₪', 'lei'];

const SUPPORTED_CODES = Object.keys(CURRENCY_CODE) as CurrencyCode[];

const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Longest first so that e.g. "HK$" wins over "$" and "R$" over "R"
const SYMBOL_ALTERNATION = [
  ...CSV_CURRENCY_SYMBOLS.map(s => s.symbol),
  ...UNSUPPORTED_EXPORT_SYMBOLS,
]
  .sort((a, b) => b.length - a.length)
  .map(escapeRegex)
  .join('|');

const TOKEN = `(?:[A-Za-z]{3}(?![A-Za-z])|${SYMBOL_ALTERNATION})`;

// [-] [token] [-] number [K|M] [token]
const BUDGET_PATTERN = new RegExp(
  `^(-)?\\s*(${TOKEN})?\\s*(-)?\\s*(\\d[\\d,]*(?:\\.\\d+)?)\\s*([KM](?![A-Za-z]))?\\s*(${TOKEN})?$`,
  'i'
);

export type CsvBudgetParseResult =
  | { currency: CurrencyCode; ok: true; value: number; ambiguousWith?: CurrencyCode[] }
  | { ok: false; reason: 'invalid' | 'negative' | 'unsupportedCurrency'; token?: string };

const resolveToken = (
  token: string,
  roadmapCurrency?: string
): { currency: CurrencyCode; ambiguousWith?: CurrencyCode[] } | null => {
  const symbol = CSV_CURRENCY_SYMBOLS.find(s => s.symbol.toLowerCase() === token.toLowerCase());
  if (symbol) {
    const { currencies } = symbol;
    if (currencies.length === 1) {
      return { currency: currencies[0] };
    }
    const preferred = currencies.find(c => c === roadmapCurrency);
    return preferred
      ? { currency: preferred }
      : { currency: currencies[0], ambiguousWith: currencies };
  }

  const code = SUPPORTED_CODES.find(c => c === token.toUpperCase());
  return code ? { currency: code } : null;
};

/**
 * Parse a budget cell such as "£1,234.56", "C$1,234.56", "1234.56 kr", "PKR1,234.56" or "£1.5M".
 * Shared symbols (¥, kr) resolve to the roadmap's currency when it is one of the candidates,
 * otherwise to the default, reported via `ambiguousWith`. A value without a currency is GBP.
 */
export const parseCsvBudget = (raw: string, roadmapCurrency?: string): CsvBudgetParseResult => {
  const match = raw.trim().match(BUDGET_PATTERN);
  if (!match) {
    return { ok: false, reason: 'invalid' };
  }

  const [, leadingMinus, prefix, innerMinus, amount, shortcut, suffix] = match;

  if (prefix && suffix) {
    return { ok: false, reason: 'invalid' };
  }
  if (leadingMinus && innerMinus) {
    return { ok: false, reason: 'invalid' };
  }

  const token = prefix || suffix;
  let resolved: ReturnType<typeof resolveToken> = { currency: 'GBP' };
  if (token) {
    resolved = resolveToken(token, roadmapCurrency);
    if (!resolved) {
      return { ok: false, reason: 'unsupportedCurrency', token };
    }
  }

  // The backend rejects negative budgets, so the exporter never writes them
  if (leadingMinus || innerMinus) {
    return { ok: false, reason: 'negative' };
  }

  const multiplier = { K: 1000, M: 1000000 }[shortcut?.toUpperCase() as 'K' | 'M'] ?? 1;
  const value = parseFloat(amount.replace(/,/g, '')) * multiplier;

  return { ok: true, value, ...resolved };
};

/**
 * The roadmap's currency, only when every budget on the roadmap uses the same one. Currency is
 * stored per initiative budget, so a roadmap can mix currencies; in that case, or when it has no
 * budgets, this returns undefined. A budget without a currency counts as GBP, the backend default.
 */
export const getRoadmapCurrency = (
  initiativeBudgets?: Map<number, { currency?: string | null }>
): string | undefined => {
  const currencies = new Set<string>();
  initiativeBudgets?.forEach(({ currency }) => {
    currencies.add(currency || CURRENCY_CODE.GBP);
  });
  return currencies.size === 1 ? Array.from(currencies)[0] : undefined;
};
